'use client';

import {useState, useEffect} from 'react';
import {useLocale} from 'next-intl';
import Card from '@/components/ui/Card';

interface AuditLog {
  id: number;
  utilisateur_id: number | null;
  action: string;
  entite: string | null;
  entite_id: number | null;
  details_json: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string | null;
}

export default function AuditPage() {
  const locale = useLocale();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtreAction, setFiltreAction] = useState('');
  const [filtreEntite, setFiltreEntite] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('utss_user');
    if (!stored) { window.location.href = `/${locale}/auth`; return; }
    const u = JSON.parse(stored);
    if (u.role !== 'admin_technique' && u.role !== 'auditeur') {
      window.location.href = `/${locale}/dashboard`; return;
    }

    const token = localStorage.getItem('utss_token');
    fetch('/api/v1/audit/?limit=200', {
      headers: token ? {Authorization: `Bearer ${token}`} : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setLogs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locale]);

  const filtered = logs.filter(l => {
    if (filtreAction && l.action !== filtreAction) return false;
    if (filtreEntite && (l.entite || '') !== filtreEntite) return false;
    return true;
  });

  const actions = [...new Set(logs.map(l => l.action).filter(Boolean))].sort();
  const entites = [...new Set(logs.map(l => l.entite || '').filter(Boolean))].sort();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-primary mb-2">Journal d&apos;audit</h1>
      <p className="text-muted text-sm mb-6">Trace non alterable de toutes les actions du système. Lecture seule.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filtreAction} onChange={e => setFiltreAction(e.target.value)} className="px-2 py-1.5 text-xs border border-border rounded-lg bg-white">
          <option value="">Toutes actions</option>
          {actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtreEntite} onChange={e => setFiltreEntite(e.target.value)} className="px-2 py-1.5 text-xs border border-border rounded-lg bg-white">
          <option value="">Toutes entites</option>
          {entites.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <span className="text-xs text-muted self-center">{filtered.length} entrees</span>
      </div>

      <Card>
        {loading ? (
          <div className="text-center py-8 text-muted">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-muted">Aucune entree d&apos;audit.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted">
                  <th className="text-start py-2 px-2">Date</th>
                  <th className="text-start py-2 px-2">Action</th>
                  <th className="text-start py-2 px-2">Entite</th>
                  <th className="text-start py-2 px-2">ID</th>
                  <th className="text-start py-2 px-2">User</th>
                  <th className="text-start py-2 px-2">IP</th>
                  <th className="text-start py-2 px-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 100).map(log => (
                  <tr key={log.id} className="border-b border-border last:border-0 hover:bg-surface/50">
                    <td className="py-2 px-2 text-xs text-muted whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString('fr-TN') : '—'}</td>
                    <td className="py-2 px-2"><span className="text-xs font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded">{log.action}</span></td>
                    <td className="py-2 px-2 text-xs">{log.entite || '—'}</td>
                    <td className="py-2 px-2 text-xs text-muted">{log.entite_id || '—'}</td>
                    <td className="py-2 px-2 text-xs text-muted">{log.utilisateur_id || '—'}</td>
                    <td className="py-2 px-2 text-xs text-muted">{log.ip_address || '—'}</td>
                    <td className="py-2 px-2 text-xs text-muted max-w-[200px] truncate">{log.details_json ? JSON.stringify(log.details_json) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
